import { m, HTMLMotionProps } from "framer-motion";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { useIsInPopover } from "src/base-components/Popover/PopoverContext";
import { useIsInFloatingWindow } from "src/datasets/DatasetTable/FloatingWindow";

/***
 *  Generic motion dropdown panel component.
 *  This component is used to animate the dropdown panel when it is opened.
 *
 *  Usage:
 * ```jsx
 * <AnimatePresence>
 *   {open && (
 *     <MenuItems as={MotionDropdownPanel}>
 *       <MenuItem>Option 1</MenuItem>
 *     </MenuItems>
 *   )}
 * </AnimatePresence>
 * ```
 */
export const MotionDropdownPanel = forwardRef<
  HTMLDivElement,
  HTMLMotionProps<"div"> & {
    children: React.ReactNode;
    exitTimeoutMs?: number;
  }
>(({ children, className, exitTimeoutMs: timeout = 0, ...props }, ref) => {
  const isInFloatingWindow = useIsInFloatingWindow();
  const isInPopover = useIsInPopover();

  return (
    <m.div
      ref={ref}
      animate={{ opacity: 1, scale: 1 }}
      className={twMerge(
        "decideScrollbar relative origin-top overflow-hidden rounded-lg bg-white py-2 shadow-lg",
        isInFloatingWindow || isInPopover ? "z-[60]" : "z-10",
        "outline-none",
        className,
      )}
      exit={{
        opacity: 0,
        scale: 0.95,
        transition: {
          delay: timeout / 1000,
        },
      }}
      initial={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      {...props}
    >
      {children}
    </m.div>
  );
});
